import React, { useCallback, useEffect, useState, useRef } from 'react'
import { makeStyles } from '@material-ui/core/styles'
import { Box, createMuiTheme, Drawer, Switch, ThemeProvider, Typography } from '@material-ui/core'
import { translate } from 'components/shared/internationalization'
import { AtlasWhite_100, ColorBaseMulberryBlue } from 'assets/styles/variables'
import { AtlasIcon } from '@diligentcorp/atlas-react'
import { IconButton } from 'components/shared/StyledComponents'
import { NewDialogTextField } from 'components/shared/StyledComponents'
import {
    getNotificationSettings,
    postNotificationSettings,
    patchNotificationSettings
} from 'businesslayer/networkrequests/notificationsSettings'
import { transientStorageManager } from '../../../businesslayer/minutesSessionStore'
import i18n from 'common/util/internationalization'

import TextLengthCounter from 'components/shared/TextLengthCounter'
import { MAX_DAYS, REMINDER_MIN_VALUE, REMINDER_DEFAULT_VALUE } from 'common/constants/constants'

const drawerWidth = 658

const theme = createMuiTheme({
    overrides: {
        MuiSwitch: {
            switchBase: {
                width: '37px',
                height: '28px',
                color: AtlasWhite_100
            },
            colorPrimary: {
                '&.Mui-checked': {
                    color: AtlasWhite_100
                }
            },
            track: {
                // Controls default (unchecked) color for the track
                opacity: 0.2,
                backgroundColor: '#455D82',
                '.Mui-checked.Mui-checked + &': {
                    // Controls checked color for the track
                    opacity: 1,
                    backgroundColor: '#455D82'
                }
            }
        }
    }
})

const useStyles = makeStyles((theme) => ({
    drawer: {
        maxWidth: drawerWidth,
        [theme.breakpoints.down('md')]: {
            maxWidth: '100vw'
        },
        '@media (max-width: 768px)': {
            width: '100vw'
        }
    },
    drawerPaper: {
        position: 'fixed',
        maxWidth: drawerWidth,
        [theme.breakpoints.down('md')]: {
            maxWidth: '100vw'
        },
        '@media (max-width: 768px)': {
            width: '100vw'
        }
    },
    headerTitle: {
        fontWeight: 600,
        fontSize: '22px',
        padding: '16px',
        lineHeight: '26px'
    },
    headerSubtitle: {
        padding: '16px 24px',
        borderTop: '1px solid #e0e0e0',
        borderBottom: '1px solid #e0e0e0'
    },
    reminderSettingsBox: {
        padding: '16px 24px'
    },
    unlinkedActionsBox: {
        padding: '16px 24px',
        borderTop: '1px solid #e0e0e0'
    },
    settingsDrawerNotificationTitle: {
        fontWeight: 600,
        fontSize: '18px'
    },
    reminderSettingsTitle: {
        fontWeight: 600,
        fontSize: '22px'
    },
    settingsDrawerNotificationSubtitle: {
        fontWeight: 400,
        fontSize: '18px',
        lineHeight: '20px',
        maxWidth: '546px'
    },
    headerBox: {
        height: '64px',
        backgroundColor: '#fff',
        zIndex: theme.zIndex.drawer + 1,
        flexShrink: 0,
        display: 'flex'
    },
    btnContainer: {
        float: 'right',
        display: 'flex',
        alignItems: 'center',
        margin: 'auto',
        marginRight: '16px',
        textDecoration: 'none'
    },
    panal: {
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        '& [class*="MuiTypography-body1"]': {
            fontFamily: 'Source Sans Pro'
        }
    },
    body: {
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        paddingTop: '24px'
    },
    reminderBody: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        paddingTop: '24px'
    },
    switch: {
        height: '28px',
        marginRight: '10px',
        marginTop: '0px',
        width: '52px',
        borderRadius: '26px',
        padding: '0px',
        cursor: 'pointer'
    },
    closeButton: {
        '&:focus, &:active': {
            padding: '2px',
            margin: '2px',
            outline: `2px solid ${ColorBaseMulberryBlue}`
        }
    },
    switchBUtton: {
        height: '30px',
        width: '80px',
        '&:focus, &:active': {
            outline: `2px solid ${ColorBaseMulberryBlue}`
        }
    },
    inputField: {
        width: 55,
        marginRight: 8,
        marginLeft: 8,
        '& [class*="MuiOutlinedInput-root"]': {
            marginTop: 0
        },
        '& [class*="MuiOutlinedInput-root"].Mui-disabled': {
            '& input': {
                color: '#999999', // Gray color for disabled text
                '-webkit-text-fill-color': '#999999' // Override webkit autofill
            }
        },
        '& input[type=number]': {
            '-moz-appearance': 'textfield'
        },
        '& input[type=number]::-webkit-outer-spin-button': {
            '-webkit-appearance': 'none',
            margin: 0
        },
        '& input[type=number]::-webkit-inner-spin-button': {
            '-webkit-appearance': 'none',
            margin: 0
        }
    },
    errorInput: {
        '& .MuiOutlinedInput-root': {
            color: 'var(--Semantic-Color-Status-Error, #AF292E)',
            fontFeatureSettings: "'liga' off, 'clig' off",
            fontFamily: 'Source Sans Pro',
            fontSize: 13,
            fontStyle: 'normal',
            fontWeight: 400,
            lineHeight: '16px' /* 123.077% */,

            borderRadius: '4px',
            border: '1px solid #af292e',

            '&:hover': {
                border: '2px solid #af292e'
            },
            '&.Mui-focused': {
                border: '2px solid #af292e'
            },
            '& .MuiOutlinedInput-notchedOutline': {
                border: 'none'
            }
        }
    },
    errorMessage: {
        color: 'var(--Semantic-Color-Status-Error, #AF292E)',
        fontFeatureSettings: "'liga' off, 'clig' off",
        fontFamily: 'Source Sans Pro',
        fontSize: 13,
        fontStyle: 'normal',
        fontWeight: 400,
        lineHeight: '16px' /* 123.077% */,
        marginTop: '20px',
        float: 'right'
    }
}))

interface SettingsDrawerProps {
    setIsOpen: React.Dispatch<React.SetStateAction<boolean>>
    isOpen: boolean
}

const SettingsDrawer: React.FC<SettingsDrawerProps> = ({ setIsOpen, isOpen }) => {
    const classes = useStyles()
    const toggleDrawer = useCallback(() => {
        setIsOpen(!isOpen)
    }, [setIsOpen, isOpen])
    const [checked, setChecked] = useState(true)
    const [actionDueDateReminder, setActionDueDateReminder] = useState(false)
    const [documentDueDateReminder, setDocumentDueDateReminder] = useState(false)
    const [documentDueDateReminderDays, setDocumentDueDateReminderDays] = useState<number | string>(
        1
    )
    const [completeUnlinkedActions, setCompleteUnlinkedActions] = useState(false)
    const [showValidationError, setShowValidationError] = useState(false)
    const [responseObjectForNotification, setResponseObjectForNotification] = useState<{
        data: any[]
    }>({ data: [] })

    // Ref to store timeout for debouncing API calls
    const apiCallTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

    const isValidValue = (value: string): boolean => {
        // Check for empty input
        if (value === '' || value.trim() === '') {
            return false
        }

        // Check for decimal
        if (value.includes('.') || value.includes(',')) {
            return false
        }

        // Check for negative
        if (value.includes('-')) {
            return false
        }

        // Convert to number and check range
        const numValue = parseInt(value, 10)
        if (isNaN(numValue) || numValue < REMINDER_MIN_VALUE || numValue > MAX_DAYS) {
            return false
        }

        return true
    }
    useEffect(() => {
        setShowValidationError(false)
    }, [isOpen])

    const validateInput = (value: string) => {
        const isValid = isValidValue(value)
        setShowValidationError(!isValid)
    }

    const handleChange = async (overrides?: {
        actionNotification?: boolean
        actionDueDateReminder?: boolean
        documentDueDateReminder?: boolean
        documentDueDateReminderDays?: number
        completeUnlinkedActions?: boolean
    }) => {
        const actionNotificationValue = overrides?.actionNotification ?? checked
        const actionDueDateReminderValue = overrides?.actionDueDateReminder ?? actionDueDateReminder
        const documentDueDateReminderValue =
            overrides?.documentDueDateReminder ?? documentDueDateReminder
        const documentDueDateReminderDaysValue =
            overrides?.documentDueDateReminderDays ?? documentDueDateReminderDays
        const completeUnlinkedActionsValue =
            overrides?.completeUnlinkedActions ?? completeUnlinkedActions

        const daysAsNumber =
            typeof documentDueDateReminderDaysValue === 'string'
                ? parseInt(documentDueDateReminderDaysValue, 10)
                : documentDueDateReminderDaysValue
        const isDaysValueValid =
            !isNaN(daysAsNumber) && daysAsNumber >= REMINDER_MIN_VALUE && daysAsNumber <= MAX_DAYS

        try {
            if (responseObjectForNotification?.data?.length === 0) {
                const payload = {
                    data: {
                        attributes: {
                            platform: transientStorageManager.platform,
                            site_name: transientStorageManager.siteName,
                            action_notification: actionNotificationValue,
                            review_reminder_on_due_date: actionDueDateReminderValue,
                            review_reminder_before_due_date: documentDueDateReminderValue,
                            complete_unlinked_actions: completeUnlinkedActionsValue,
                            ...(documentDueDateReminderValue &&
                                isDaysValueValid && {
                                    notification_days_before_due: daysAsNumber
                                })
                        }
                    }
                }

                const createNotificationSettings = async () => {
                    const response = await postNotificationSettings(JSON.stringify(payload))
                    if (response) {
                        setResponseObjectForNotification({ data: [response.data] })
                    }
                }
                createNotificationSettings()
            } else {
                const payload = {
                    data: {
                        attributes: {
                            action_notification: actionNotificationValue,
                            review_reminder_on_due_date: actionDueDateReminderValue,
                            review_reminder_before_due_date: documentDueDateReminderValue,
                            complete_unlinked_actions: completeUnlinkedActionsValue,
                            ...(documentDueDateReminderValue &&
                                isDaysValueValid && {
                                    notification_days_before_due: daysAsNumber
                                })
                        }
                    }
                }

                const updateNotificationSettings = async () => {
                    const response = await patchNotificationSettings(
                        JSON.stringify(payload),
                        responseObjectForNotification.data[0]?.id
                    )
                    if (response) {
                        setResponseObjectForNotification({ data: [response.data] })
                    }
                }
                updateNotificationSettings()
            }
        } catch (error) {
            console.error('Error updating settings:', error)
        }
    }

    useEffect(() => {
        const getSettings = async () => {
            const minutesDetails = await getNotificationSettings()
            setResponseObjectForNotification(minutesDetails)
            if (minutesDetails.data[0]?.attributes?.action_notification === false) {
                setChecked(false)
            } else if (
                (minutesDetails.data && minutesDetails.data.length === 0) ||
                minutesDetails.data[0]?.attributes?.action_notification === true
            ) {
                setChecked(true)
            }

            if (minutesDetails.data[0]?.attributes?.review_reminder_on_due_date === true) {
                setActionDueDateReminder(true)
            } else if (minutesDetails.data[0]?.attributes?.review_reminder_on_due_date === false) {
                setActionDueDateReminder(false)
            } else {
                setActionDueDateReminder(true)
            }

            if (minutesDetails.data[0]?.attributes?.review_reminder_before_due_date === true) {
                setDocumentDueDateReminder(true)
            } else if (
                minutesDetails.data[0]?.attributes?.review_reminder_before_due_date === false
            ) {
                setDocumentDueDateReminder(false)
            } else {
                setDocumentDueDateReminder(true)
            }

            if (minutesDetails.data[0]?.attributes?.complete_unlinked_actions === true) {
                setCompleteUnlinkedActions(true)
            } else if (minutesDetails.data[0]?.attributes?.complete_unlinked_actions === false) {
                setCompleteUnlinkedActions(false)
            } else {
                setCompleteUnlinkedActions(false)
            }

            const daysFromDb = minutesDetails.data[0]?.attributes?.notification_days_before_due
            if (
                daysFromDb &&
                typeof daysFromDb === 'number' &&
                daysFromDb >= REMINDER_MIN_VALUE &&
                daysFromDb <= MAX_DAYS
            ) {
                setDocumentDueDateReminderDays(daysFromDb)
            } else {
                setDocumentDueDateReminderDays(REMINDER_DEFAULT_VALUE)
            }
        }

        if (isOpen) {
            getSettings()
        }
    }, [isOpen])

    useEffect(() => {
        return () => {
            if (apiCallTimeoutRef.current) {
                clearTimeout(apiCallTimeoutRef.current)
            }
        }
    }, [])
    return (
        <Drawer
            className={classes.drawer}
            classes={{ paper: classes.drawerPaper }}
            anchor="right"
            open={isOpen}
            onClose={toggleDrawer}>
            <div className={classes.panal}>
                <Box className={classes.headerBox}>
                    <Typography className={classes.headerTitle}>{translate('SETTINGS')}</Typography>
                    <Box className={classes.btnContainer}>
                        <IconButton
                            disableFocusRipple
                            disableRipple
                            onClick={() => setIsOpen(!isOpen)}>
                            <div
                                className={classes.closeButton}
                                role="button"
                                aria-label="Close"
                                tabIndex={0}
                                onClick={() => setIsOpen(!isOpen)}
                                onKeyUp={(event) => {
                                    if (event.key === 'Enter' || event.key === ' ') {
                                        setIsOpen(!isOpen)
                                    }
                                }}>
                                <AtlasIcon data-testid="AtlasModal_CloseIcon" name="close" />
                            </div>
                        </IconButton>
                    </Box>
                </Box>

                <Box className={classes.headerSubtitle}>
                    <Typography className={classes.reminderSettingsTitle}>
                        {translate('NOTIFICATION_LABEL')}
                    </Typography>
                    <Box className={classes.body}>
                        <ThemeProvider theme={theme}>
                            <div
                                role="switch"
                                className={classes.switchBUtton}
                                data-analytics="MM-NotificationSettingsSwitch"
                                aria-checked={checked}
                                tabIndex={0}
                                onKeyPressCapture={(event) => {
                                    if (event.key === 'Enter' || event.key === ' ') {
                                        const newValue = !checked
                                        setChecked(newValue)
                                        setTimeout(
                                            () => handleChange({ actionNotification: newValue }),
                                            0
                                        )
                                    }
                                }}
                                onClick={() => {
                                    const newValue = !checked
                                    setChecked(newValue)
                                    setTimeout(
                                        () => handleChange({ actionNotification: newValue }),
                                        0
                                    )
                                }}>
                                <Switch
                                    className={classes.switch}
                                    checked={checked}
                                    onChange={() => {}}
                                    aria-label={i18n.t('NOTIFICATION_SETTINGS')}
                                    color="primary"
                                    inputProps={
                                        {
                                            'aria-label': i18n.t('NOTIFICATION_SETTINGS'),
                                            'data-analytics': 'MM-NotificationSettingsSwitch'
                                        } as React.InputHTMLAttributes<HTMLInputElement>
                                    }
                                />
                            </div>
                        </ThemeProvider>
                        <Box>
                            <Typography className={classes.settingsDrawerNotificationTitle}>
                                {translate('ALLOW_EMAIL')}
                            </Typography>
                            <Typography className={classes.settingsDrawerNotificationSubtitle}>
                                {translate('SEND_EMAIL_NOTIFICATIONS')}
                            </Typography>
                        </Box>
                    </Box>
                </Box>

                <Box className={classes.reminderSettingsBox}>
                    <Typography className={classes.reminderSettingsTitle}>
                        {translate('REMINDER_LABEL')}
                    </Typography>

                    <Typography className={classes.settingsDrawerNotificationTitle}>
                        {translate('REVIEWS')}
                    </Typography>

                    <Box className={classes.reminderBody}>
                        <ThemeProvider theme={theme}>
                            <div
                                role="switch"
                                className={classes.switchBUtton}
                                data-analytics="MM-Reminder-SendReminderOnDueDate"
                                aria-checked={actionDueDateReminder}
                                tabIndex={0}
                                onClick={() => {
                                    const newValue = !actionDueDateReminder
                                    setActionDueDateReminder(newValue)
                                    setTimeout(
                                        () => handleChange({ actionDueDateReminder: newValue }),
                                        0
                                    )
                                }}>
                                <Switch
                                    className={classes.switch}
                                    checked={actionDueDateReminder}
                                    onChange={() => {}}
                                    aria-label={i18n.t('SEND_REMINDER_ON_DUE_DATE')}
                                    color="primary"
                                    inputProps={
                                        {
                                            'aria-label': i18n.t('SEND_REMINDER_ON_DUE_DATE'),
                                            'data-analytics': 'MM-Reminder-SendReminderOnDueDate'
                                        } as React.InputHTMLAttributes<HTMLInputElement>
                                    }
                                />
                            </div>
                        </ThemeProvider>
                        <Box>
                            <Typography className={classes.settingsDrawerNotificationSubtitle}>
                                {translate('SEND_REMINDER_ON_DUE_DATE')}
                            </Typography>
                        </Box>
                    </Box>

                    <Box className={classes.reminderBody}>
                        <ThemeProvider theme={theme}>
                            <div
                                role="switch"
                                className={classes.switchBUtton}
                                data-analytics="MM-Reminder-SendReminderBeforeDueDate"
                                aria-checked={documentDueDateReminder}
                                tabIndex={0}
                                onClick={() => {
                                    const newValue = !documentDueDateReminder
                                    setDocumentDueDateReminder(newValue)
                                    setTimeout(
                                        () => handleChange({ documentDueDateReminder: newValue }),
                                        0
                                    )
                                }}>
                                <Switch
                                    className={classes.switch}
                                    checked={documentDueDateReminder}
                                    onChange={() => {}}
                                    aria-label={i18n.t('SEND_REMINDER_ON_DUE_DATE')}
                                    color="primary"
                                    inputProps={
                                        {
                                            'aria-label': i18n.t('SEND_REMINDER_BEFORE_DUE_DATE'),
                                            'data-analytics':
                                                'MM-Reminder-SendReminderBeforeDueDate'
                                        } as React.InputHTMLAttributes<HTMLInputElement>
                                    }
                                />
                            </div>
                        </ThemeProvider>
                        <Box>
                            <Box display="flex" alignItems="center" flexWrap="wrap">
                                <Typography className={classes.settingsDrawerNotificationSubtitle}>
                                    {translate('SEND_REMINDER_BEFORE_DUE_DATE')}
                                </Typography>
                                <span>
                                    <NewDialogTextField
                                        className={`${classes.inputField} ${
                                            showValidationError ? classes.errorInput : ''
                                        }`}
                                        type="number"
                                        variant="outlined"
                                        size="small"
                                        inputProps={{
                                            min: REMINDER_MIN_VALUE,
                                            style: { marginTop: 0 }
                                        }}
                                        value={documentDueDateReminderDays}
                                        disabled={!documentDueDateReminder}
                                        onChange={(e) => {
                                            const inputValue = e.target.value

                                            setDocumentDueDateReminderDays(inputValue)

                                            validateInput(inputValue)

                                            if (apiCallTimeoutRef.current) {
                                                clearTimeout(apiCallTimeoutRef.current)
                                                apiCallTimeoutRef.current = null
                                            }

                                            if (inputValue !== '' && isValidValue(inputValue)) {
                                                const val = parseInt(inputValue, 10)
                                                apiCallTimeoutRef.current = setTimeout(() => {
                                                    handleChange({
                                                        documentDueDateReminderDays: val
                                                    })
                                                }, 2000)
                                            }
                                        }}
                                    />
                                </span>
                                <Typography>{translate('NO_OF_DAYS')}</Typography>
                            </Box>
                        </Box>
                    </Box>
                    <span className={classes.errorMessage}>
                        <TextLengthCounter
                            errorText={
                                showValidationError ? i18n.t('REMINDER_DATES_WITHIN_A_YEAR') : ''
                            }
                            maxValue={0}
                            value={''}
                            colour={showValidationError}
                        />
                    </span>
                </Box>

                <Box className={classes.unlinkedActionsBox}>
                    <Typography className={classes.reminderSettingsTitle}>
                        {translate('COMPLETE_UNLINKED_ACTIONS')}
                    </Typography>

                    <Box className={classes.reminderBody}>
                        <ThemeProvider theme={theme}>
                            <div
                                role="switch"
                                className={classes.switchBUtton}
                                data-analytics="MM-CompleteUnlinkedActionsSwitch"
                                aria-checked={completeUnlinkedActions}
                                tabIndex={0}
                                onKeyPressCapture={(event) => {
                                    if (event.key === 'Enter' || event.key === ' ') {
                                        const newValue = !completeUnlinkedActions
                                        setCompleteUnlinkedActions(newValue)
                                        setTimeout(
                                            () =>
                                                handleChange({
                                                    completeUnlinkedActions: newValue
                                                }),
                                            0
                                        )
                                    }
                                }}
                                onClick={() => {
                                    const newValue = !completeUnlinkedActions
                                    setCompleteUnlinkedActions(newValue)
                                    setTimeout(
                                        () => handleChange({ completeUnlinkedActions: newValue }),
                                        0
                                    )
                                }}>
                                <Switch
                                    className={classes.switch}
                                    checked={completeUnlinkedActions}
                                    onChange={() => {}}
                                    aria-label={i18n.t('COMPLETE_UNLINKED_ACTIONS')}
                                    color="primary"
                                    inputProps={
                                        {
                                            'aria-label': i18n.t('COMPLETE_UNLINKED_ACTIONS'),
                                            'data-analytics': 'MM-CompleteUnlinkedActionsSwitch'
                                        } as React.InputHTMLAttributes<HTMLInputElement>
                                    }
                                />
                            </div>
                        </ThemeProvider>
                        <Box>
                            <Typography className={classes.settingsDrawerNotificationSubtitle}>
                                {translate('COMPLETE_UNLINKED_ACTIONS_INFO')}
                            </Typography>
                        </Box>
                    </Box>
                </Box>
            </div>
        </Drawer>
    )
}

export default SettingsDrawer
